import { useMutation, useMutationState, useQueryClient } from "@tanstack/react-query";

import { errorHandler } from "#app/monitoring/errorHandler";
import { setLayoutNestingQueryData } from "#modules/layouts/infra/layoutNesting.query";
import { setOptimizationsQueryData } from "#modules/optimizations/infra/optimizations.query";
import { runOptimizationNesting } from "#modules/optimizations/infra/runOptimizationNesting.api";
import { queryKeys } from "#shared/queryKeys/queryKeys";
import { NestingRunView } from "shared";

type Params = {
  quoteRequestId: string;
  optimizationId: string;
  onError?: (err: unknown) => void;
};

export const useRunOptimizationNesting = ({ quoteRequestId, optimizationId, onError }: Params) => {
  const queryClient = useQueryClient();

  const mutationKey = ["runOptimizationNesting", optimizationId];

  const { mutate } = useMutation({
    mutationKey,
    mutationFn: runOptimizationNesting,
    onSuccess: (nestingRun) => {
      const { layoutId } = nestingRun;
      setLayoutNestingQueryData(queryClient, layoutId, nestingRun);
      void queryClient.invalidateQueries({ queryKey: queryKeys.layouts.detail(layoutId) });
      setOptimizationsQueryData(queryClient, quoteRequestId, (optimizations) =>
        optimizations?.map((optimization) =>
          optimization.id === optimizationId && optimization.sheetNestingOptimizationMetadata
            ? {
                ...optimization,
                sheetNestingOptimizationMetadata: {
                  ...optimization.sheetNestingOptimizationMetadata,
                  layoutId,
                },
              }
            : optimization,
        ),
      );
    },
    onError: (err: unknown) => {
      onError?.(err);
      errorHandler(err);
    },
  });

  const [lastMutation] = useMutationState({
    filters: { mutationKey },
    select: ({ state }) => ({
      status: state.status,
      data: state.data as NestingRunView | undefined,
    }),
  }).slice(-1);

  return {
    runOptimizationNesting: mutate,
    data: lastMutation?.data,
    isPending: lastMutation?.status === "pending",
    isError: lastMutation?.status === "error",
  };
};
